import { createSlice } from "@reduxjs/toolkit";
import { fetchWatchlistThunk, followStockThunk, unfollowStockThunk } from "./watchlistActions";

const initialState = {
  items: [],
  total: 0,
  loading: false,
  error: null,
  fetched: false,         // prevent repeat fetch spam
  lastFetchTime: null,    // track when last fetch occurred
  lastFetchParams: null,  // track what params were used
};

const watchlistSlice = createSlice({
  name: "watchlist",
  initialState,
  reducers: {
    resetWatchlist: (state) => {
      state.items = [];
      state.total = 0;
      state.loading = false;
      state.error = null;
      state.fetched = false;
      state.lastFetchTime = null;
      state.lastFetchParams = null;
    },
    addItem: (state, action) => {
      const { stockId, symbol } = action.payload;
      if (!state.items.find((s) => s.symbol === symbol)) {
        state.items.push({ id: stockId, symbol });
        state.total += 1;
      }
    },
    removeItem: (state, action) => {
      const { symbol } = action.payload;
      state.items = state.items.filter((s) => s.symbol !== symbol);
      state.total = Math.max(0, state.total - 1);
    },
  },
  extraReducers: (builder) => {
    // Fetch Watchlist
    builder
      .addCase(fetchWatchlistThunk.pending, (state, action) => {
        // The thunk's own `condition` option is now the single source of
        // truth for debouncing -- this only ever fires for a call that's
        // already been let through, so no skip-check is needed here.
        state.loading = true;
        state.error = null;
        state.lastFetchParams = action.meta.arg || {};
      })
      .addCase(fetchWatchlistThunk.fulfilled, (state, action) => {
        console.log(
          "✅ Watchlist: Fetch completed, received:",
          action.payload.items?.length || 0,
          "items"
        );
        state.items = action.payload.items || [];
        state.total = action.payload.total || 0;
        state.loading = false;
        state.fetched = true;
        state.lastFetchTime = Date.now();
      })
      .addCase(fetchWatchlistThunk.rejected, (state, action) => {
        // Was action.error.message -- when a thunk uses rejectWithValue
        // (as this one's action creator does), RTK's action.error.message
        // is just the generic string "Rejected", not the real captured
        // message, which lives in action.payload instead.
        console.warn("❌ Watchlist: Fetch failed:", action.payload || action.error.message);
        state.loading = false;
        state.error = action.payload || action.error.message || "Failed to load watchlist";
      });

    // Follow Stock
    builder
      .addCase(followStockThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(followStockThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { stockId, symbol } = action.payload;
        if (!state.items.find((s) => s.symbol === symbol)) {
          state.items.push({ id: stockId, symbol });
          state.total += 1;
        }
      })
      .addCase(followStockThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to follow stock";
      });

    // Unfollow Stock
    builder
      .addCase(unfollowStockThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unfollowStockThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { symbol } = action.payload;
        state.items = state.items.filter((s) => s.symbol !== symbol);
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(unfollowStockThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to unfollow stock";
      });
  },
});

export const { resetWatchlist, addItem, removeItem } = watchlistSlice.actions;
export default watchlistSlice.reducer;
